/**
 * CrossmintApiService - Handles interaction with Crossmint API
 */

import { z } from "zod/v4";
import type { AuthData } from "../../types";
import type { EncryptionService } from "../encryption";
import type { FrameLogger } from "../logger";
import { CrossmintFrameService } from "../service";
import type { RetryConfig } from "./backoff";
import { calculateBackoff, defaultRetryConfig, shouldRetry } from "./backoff";
import { type Environment, getEnvironment } from "./environment";
import { CrossmintHttpError, CrossmintRequest } from "./request";

/**
 * CrossmintAPIError is thrown when an API error should be shown to the user.
 * This extends the generic ClientFacingError pattern for API-specific errors.
 */
export class CrossmintAPIError extends Error {
	constructor(
		public readonly clientMessage: string,
		public readonly code?: string,
		public readonly data?: unknown,
	) {
		super(clientMessage);
		this.name = "CrossmintAPIError";
	}
}

/**
 * Maps CrossmintHttpError to CrossmintAPIError for user-facing error handling.
 * Currently handles 429 rate limit errors from the TEE service.
 */
function mapApiError(error: unknown): never {
	if (error instanceof CrossmintHttpError && error.status === 429) {
		let message = "Rate limit exceeded. Please try again later.";
		let retryAfterHours: number | undefined;

		if (error.responseBody && typeof error.responseBody === "object") {
			const body = error.responseBody as {
				error?: unknown;
				retryAfterHours?: unknown;
			};

			if (typeof body.error === "string") {
				message = body.error;
			}

			if (typeof body.retryAfterHours === "number") {
				retryAfterHours = body.retryAfterHours;
			}
		}

		throw new CrossmintAPIError(message, "rate-limit", {
			retryAfterHours,
			canRetry: true,
		});
	}
	throw error;
}

function getHeaders(authData?: AuthData) {
	return {
		"Content-Type": "application/json",
		...(window?.crossmintAppId != null
			? { "x-app-identifier": window?.crossmintAppId }
			: {}),
		...(authData != null
			? {
					Authorization: `Bearer ${authData.jwt}`,
					"x-api-key": authData.apiKey,
				}
			: {}),
	};
}

// Export parseApiKey function to make it accessible for tests
export function parseApiKey(apiKey: string): {
	origin: "server" | "client";
	environment: Environment;
} {
	let origin: "server" | "client";
	switch (apiKey.slice(0, 2)) {
		case "sk":
			origin = "server";
			break;
		case "ck":
			origin = "client";
			break;
		default:
			throw new Error("Invalid API key. Invalid origin");
	}

	const apiKeyWithoutOrigin = apiKey.slice(3);
	const envs = ["development", "staging", "production"] as const;
	const environment = envs.find((env) => apiKeyWithoutOrigin.startsWith(env));
	if (!environment) {
		throw new Error("Invalid API key. Invalid environment");
	}

	return {
		origin,
		environment,
	};
}

export class CrossmintApiService extends CrossmintFrameService {
	name = "Crossmint API Service";
	log_prefix = "[CrossmintApiService]";
	private retryConfig: RetryConfig;
	private encryptionService: EncryptionService | null = null;

	constructor(protected readonly logger: FrameLogger) {
		super(logger);
		this.retryConfig = defaultRetryConfig;
	}

	async init() {}

	setEncryptionService(encryptionService: EncryptionService) {
		this.encryptionService = encryptionService;
	}

	private ensureEncryptionService(): EncryptionService {
		if (this.encryptionService == null) {
			throw new Error("Encryption service not initialized. Call init() first.");
		}
		return this.encryptionService;
	}

	// Zod schemas
	static startOnboardingInputSchema = z.object({
		authId: z.string(),
		deviceId: z.string(),
		channel: z.enum(["sms", "whatsapp"]).optional(),
		encryptionContext: z.object({
			publicKey: z.string(),
		}),
	});
	static startOnboardingOutputSchema = z.object({});

	static completeOnboardingInputSchema = z.object({
		publicKey: z.string(),
		onboardingAuthentication: z.object({
			otp: z.string(),
		}),
		deviceId: z.string(),
		/**
		 * Recovery method being onboarded, sent in the clear so the relay can label the record
		 * until the TEE reports the method it verified.
		 */
		authId: z.string().optional(),
	});

	static getDeviceInputSchema = z.undefined();
	static getDeviceOutputSchema = z.object({
		signerId: z.string(),
		deviceId: z.string(),
		encryptedMasterSecret: z.object({
			ciphertext: z.string(),
			encapsulatedKey: z.string(),
			publicKey: z.string(),
		}),
	});

	static exportDeviceInputSchema = z.object({
		walletAddress: z.string().optional(),
		authId: z.string().optional(),
	});

	static getAttestationInputSchema = z.undefined();
	static getAttestationOutputSchema = z.object({
		publicKey: z.string(),
		timestamp: z.number(),
		quote: z.string(),
		event_log: z.string(),
		hash_algorithm: z.literal("sha512"),
		prefix: z.literal("app-data"),
		collateral: z.object({
			pck_crl_issuer_chain: z.string(),
			root_ca_crl: z.string(),
			pck_crl: z.string(),
			tcb_info_issuer_chain: z.string(),
			tcb_info: z.string(),
			tcb_info_signature: z.string(),
			qe_identity_issuer_chain: z.string(),
			qe_identity: z.string(),
			qe_identity_signature: z.string(),
		}),
	});

	static getPublicKeyInputSchema = z.undefined();
	static getPublicKeyOutputSchema = z.object({
		publicKey: z.string(),
	});

	async startOnboarding(
		input: z.infer<typeof CrossmintApiService.startOnboardingInputSchema>,
		authData: AuthData,
	) {
		CrossmintApiService.startOnboardingInputSchema.parse(input);
		const request = new CrossmintRequest({
			name: "startOnboarding",
			inputSchema: CrossmintApiService.startOnboardingInputSchema,
			outputSchema: CrossmintApiService.startOnboardingOutputSchema,
			environment: parseApiKey(authData.apiKey).environment,
			authData,
			endpoint: () => "/signers/start-onboarding",
			method: "POST",
			encrypted: false,
			encryptionService: this.ensureEncryptionService(),
			getHeaders,
			logger: this.logger,
		});
		try {
			return await request.execute({
				...input,
				channel: input.channel ?? "sms",
			});
		} catch (error) {
			mapApiError(error);
		}
	}

	async completeOnboarding(
		input: z.infer<typeof CrossmintApiService.completeOnboardingInputSchema>,
		authData: AuthData,
	): Promise<z.infer<typeof CrossmintApiService.getDeviceOutputSchema>> {
		CrossmintApiService.completeOnboardingInputSchema.parse(input);
		const request = new CrossmintRequest({
			name: "completeOnboarding",
			authData,
			inputSchema: CrossmintApiService.completeOnboardingInputSchema,
			outputSchema: CrossmintApiService.getDeviceOutputSchema,
			environment: parseApiKey(authData.apiKey).environment,
			endpoint: () => "/signers/complete-onboarding",
			method: "POST",
			encrypted: true,
			unencryptedFields: ["authId"],
			encryptionService: this.ensureEncryptionService(),
			getHeaders,
			logger: this.logger,
		});
		try {
			return await request.execute(input);
		} catch (error) {
			mapApiError(error);
		}
	}

	async getAttestation(): Promise<
		z.infer<typeof CrossmintApiService.getAttestationOutputSchema>
	> {
		const request = new CrossmintRequest({
			name: "getAttestation",
			inputSchema: CrossmintApiService.getAttestationInputSchema,
			outputSchema: CrossmintApiService.getAttestationOutputSchema,
			environment: getEnvironment(),
			endpoint: () => "/attestation",
			method: "GET",
			encrypted: false,
			encryptionService: this.ensureEncryptionService(),
			getHeaders,
			logger: this.logger,
		});
		return request.execute(undefined);
	}

	async getPublicKey(): Promise<
		z.infer<typeof CrossmintApiService.getPublicKeyOutputSchema>
	> {
		const request = new CrossmintRequest({
			name: "getPublicKey",
			inputSchema: CrossmintApiService.getPublicKeyInputSchema,
			outputSchema: CrossmintApiService.getPublicKeyOutputSchema,
			environment: getEnvironment(),
			endpoint: () => "/attestation/public-key",
			method: "GET",
			encrypted: false,
			encryptionService: this.ensureEncryptionService(),
			getHeaders,
			logger: this.logger,
		});
		return request.execute(undefined);
	}

	/**
	 * @param authId - Recovery method whose record is wanted. Without it the relay returns the
	 * device's legacy record, the only one SDKs that predate authId can use.
	 */
	async getDevice(
		deviceId: string,
		input: z.infer<typeof CrossmintApiService.getDeviceInputSchema>,
		authData: AuthData,
		authId?: string,
	): Promise<z.infer<typeof CrossmintApiService.getDeviceOutputSchema>> {
		CrossmintApiService.getDeviceInputSchema.parse(input);
		const query = authId != null ? `?authId=${encodeURIComponent(authId)}` : "";
		const request = new CrossmintRequest({
			name: "getDevice",
			inputSchema: CrossmintApiService.getDeviceInputSchema,
			outputSchema: CrossmintApiService.getDeviceOutputSchema,
			environment: parseApiKey(authData.apiKey).environment,
			authData,
			endpoint: () => `/devices/${deviceId}${query}`,
			method: "GET",
			encrypted: false,
			encryptionService: this.ensureEncryptionService(),
			getHeaders,
			logger: this.logger,
		});
		try {
			return await request.execute(undefined);
		} catch (error) {
			mapApiError(error);
		}
	}

	async exportDevice(
		deviceId: string,
		input: z.infer<typeof CrossmintApiService.exportDeviceInputSchema>,
		authData: AuthData,
	): Promise<z.infer<typeof CrossmintApiService.getDeviceOutputSchema>> {
		CrossmintApiService.exportDeviceInputSchema.parse(input);
		const request = new CrossmintRequest({
			name: "exportDevice",
			inputSchema: CrossmintApiService.exportDeviceInputSchema,
			outputSchema: CrossmintApiService.getDeviceOutputSchema,
			environment: parseApiKey(authData.apiKey).environment,
			authData,
			endpoint: () => `/devices/${deviceId}/export`,
			method: "POST",
			encrypted: false,
			encryptionService: this.ensureEncryptionService(),
			getHeaders,
			logger: this.logger,
		});
		try {
			return await request.execute(input);
		} catch (error) {
			mapApiError(error);
		}
	}

	protected async fetchWithRetry(
		url: string,
		options: RequestInit,
		retryCount = 0,
	): Promise<Response> {
		try {
			const response = await fetch(url, options);

			// Check if we need to retry based on status code
			if (shouldRetry(response.status, retryCount, this.retryConfig)) {
				// Calculate delay with respect to Retry-After header if present (especially for 429)
				const retryAfterHeader = response.headers.get("Retry-After");
				const delayMs = calculateBackoff(
					retryCount,
					this.retryConfig,
					retryAfterHeader || undefined,
				);

				// Wait before retrying
				await new Promise((resolve) => setTimeout(resolve, delayMs));

				// Recursive retry with incremented retry count
				return this.fetchWithRetry(url, options, retryCount + 1);
			}

			return response;
		} catch (error) {
			// Handle network errors (will retry for network issues too)
			if (retryCount < this.retryConfig.maxRetries) {
				const delayMs = calculateBackoff(retryCount, this.retryConfig);
				await new Promise((resolve) => setTimeout(resolve, delayMs));
				return this.fetchWithRetry(url, options, retryCount + 1);
			}

			// If max retries reached, throw the error
			throw error;
		}
	}
}
